import { useId } from 'react'
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material'
import { X } from '@phosphor-icons/react'

/**
 * Diálogo reutilizable de Torre con encabezado, botón de cierre y contenido personalizado.
 * El componente padre administra los datos, formularios y acciones del diálogo.
 *
 * `open` controla la visibilidad y `onClose` debe actualizar ese estado para cerrarlo.
 * `title` define el título; `subtitle` e `icon` son opcionales.
 * `children` contiene el cuerpo y `footer` permite agregar botones u otras acciones al pie.
 * `maxWidth` acepta los tamaños de Dialog de MUI y usa 'sm' por defecto.
 * `titleId` permite definir el identificador accesible del título; si se omite, se genera uno.
 * `className` y `paperClassName` agregan clases al modal y a su panel, respectivamente.
 *
 * Ejemplo (open y setOpen pertenecen al estado del componente padre):
 * <UbiquaTowerDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title="Confirmar acción"
 *   subtitle="Revisa los datos antes de continuar."
 *   footer={<button type="button" onClick={handleConfirm}>Confirmar</button>}
 * >
 *   <p>Contenido del diálogo.</p>
 * </UbiquaTowerDialog>
 */
export default function UbiquaTowerDialog({
  open,
  onClose,
  title,
  subtitle,
  icon,
  titleId,
  children,
  footer,
  className = '',
  paperClassName = '',
  maxWidth = 'sm'
}) {
  const generatedId = useId()
  const headingId = titleId || generatedId
  const descriptionId = subtitle ? `${headingId}-description` : undefined
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      className={`ubiqua-tower-dialog-modal ${className}`}
      slotProps={{ paper: { className: `ubiqua-tower-dialog ${paperClassName}` } }}
    >
      <DialogTitle id={headingId} className="ubiqua-tower-dialog__header">
        {icon && <span className="ubiqua-tower-dialog__icon">{icon}</span>}
        <span className="ubiqua-tower-dialog__title">
          <strong>{title}</strong>
          {subtitle && <small id={descriptionId}>{subtitle}</small>}
        </span>
        <IconButton onClick={onClose} aria-label="Cerrar">
          <X size={16} />
        </IconButton>
      </DialogTitle>
      <DialogContent className="ubiqua-tower-dialog__content">{children}</DialogContent>
      {footer}
    </Dialog>
  )
}
